import React, { useState } from "react";
import { Constants } from "@eagerdog/constants";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";
import ContextMenu, { IContextMenuOption } from "src/components/ContextMenu/ContextMenu";
import { download } from "src/components/Download/DownloadManager";
import { Modal, useModal } from "src/components/Modal/Modal";
import Checkbox from "src/components/Checkbox/Checkbox";
import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";

import type { CustomCellRendererProps } from "ag-grid-react";

import styles from "./RefundRequestActionsRenderer.module.scss";

interface CustomRefundCellRendererProps extends CustomCellRendererProps {
  value: {
    request: any;
    eventId: string;
    currency?: string;
    onRefresh: () => void;
    hasEntryForms?: boolean;
  };
}

interface ApproveConfirmModalProps {
  request: any;
  currency: string;
  money: Intl.NumberFormat;
  removeEntry: boolean;
  onToggleRemoveEntry: () => void;
  useCustomAmount: boolean;
  onToggleCustomAmount: () => void;
  customAmount: string;
  onCustomAmountChange: (value: string) => void;
  customEntryCents: number;
  customPlatformCents: number;
  approveTotalCents: number;
  maxCustomAmount: number;
  feesChargedToCustomer: boolean;
  customAmountInvalid: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  processing: boolean;
}

function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}

function formatShowDate(raw: Date | string | undefined): string {
  if (!raw) return "";
  const d = new Date(raw);
  const weekday = new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(d);
  const month = new Intl.DateTimeFormat("en-US", { month: "short" }).format(d);
  const day = d.getUTCDate();
  const year = d.getUTCFullYear();
  return `${weekday}. ${month}. ${ordinal(day)}, ${year}`;
}

function buildEntryDescriptor(request: any): string {
  const parts: string[] = [formatShowDate(request.show_date)];
  if (request.show_element) parts.push(request.show_element);
  if (request.level) parts.push(request.level);
  if (request.section) parts.push(request.section);
  return parts.join(" - ");
}

const ApproveConfirmModal: React.FC<ApproveConfirmModalProps> = ({
  request,
  currency,
  money,
  removeEntry,
  onToggleRemoveEntry,
  useCustomAmount,
  onToggleCustomAmount,
  customAmount,
  onCustomAmountChange,
  customEntryCents,
  customPlatformCents,
  approveTotalCents,
  maxCustomAmount,
  feesChargedToCustomer,
  customAmountInvalid,
  onConfirm,
  onCancel,
  processing,
}) => (
  <div className={styles.confirmModal}>
    <div className={styles.title}>Approve Refund</div>
    <p className={styles.description}>
      You are about to approve a refund of{" "}
      <strong>{money.format(approveTotalCents / 100)}</strong>{" "}
      for
    </p>
    <p className={styles.entryDescriptor}>
      <strong>{request.dog_name}</strong>
      {" — "}
      {buildEntryDescriptor(request)}
    </p>
    <div className={styles.checkboxRow}>
      <Checkbox
        id="useCustomRefundAmount"
        value={useCustomAmount}
        onChange={onToggleCustomAmount}
        label="Enter a specific amount"
      />
    </div>
    {useCustomAmount && (
      <div className={styles.customAmount}>
        <Input
          type="number"
          min="0.01"
          max={maxCustomAmount}
          step=".01"
          label={
            (feesChargedToCustomer ? "Entry Fee Amount to Refund (" : "Custom Amount (") +
            currency +
            ")"
          }
          value={customAmount}
          onChange={(e: any) => onCustomAmountChange(e.target.value)}
          placeholder="0.00"
        />
        {feesChargedToCustomer && customEntryCents > 0 && (
          <div className={styles.amountBreakdown}>
            <div className={styles.breakdownRow}>
              <span>Entry fee refunded by the club:</span>
              <span>{money.format(customEntryCents / 100)}</span>
            </div>
            <div className={styles.breakdownRow}>
              <span>Eager Dog fee returned by Eager Dog:</span>
              <span>+{money.format(customPlatformCents / 100)}</span>
            </div>
            <div className={`${styles.breakdownRow} ${styles.remaining}`}>
              <span>Exhibitor receives:</span>
              <span>{money.format(approveTotalCents / 100)}</span>
            </div>
          </div>
        )}
        {customAmountInvalid && (
          <p className={styles.amountError}>
            Enter an amount greater than 0 and up to {money.format(maxCustomAmount)}.
          </p>
        )}
      </div>
    )}
    <div className={styles.checkboxRow}>
      <Checkbox
        id="removeEntry"
        value={removeEntry}
        onChange={onToggleRemoveEntry}
        label="Remove this entry from the event"
      />
    </div>
    {removeEntry && (
      <p className={styles.removeWarning}>
        This entry will be removed from the event and their entry will no longer appear in results, reports, or registrant lists.
      </p>
    )}
    <div className={styles.buttonGroup}>
      <Button onClick={onCancel} disabled={processing}>
        Cancel
      </Button>
      <Button onClick={onConfirm} disabled={processing || customAmountInvalid}>
        {processing ? "Processing…" : `Approve Refund (${money.format(approveTotalCents / 100)})`}
      </Button>
    </div>
  </div>
);

export const RefundRequestActionsRenderer = (props: CustomRefundCellRendererProps) => {
  const { request, eventId, currency = "USD", onRefresh, hasEntryForms = true } = props.value ?? {};
  const [processing, setProcessing] = useState(false);
  const [removeEntry, setRemoveEntry] = useState(true);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const { isShown, toggle } = useModal();
  const isRequested = request?.status === Constants.refund_request_status.requested;

  const money = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const requestedEntryCents = request?.entry_fee_cents ?? 0;
  const requestedPlatformCents = request?.platform_fee_cents ?? 0;
  const requestedTotalCents = request?.total_refund_cents ?? requestedEntryCents;
  const feesChargedToCustomer = requestedPlatformCents > 0;
  const maxCustomAmount = requestedEntryCents / 100;
  const customEntryCents = Math.round((parseFloat(customAmount) || 0) * 100);
  const customPlatformCents =
    feesChargedToCustomer && requestedEntryCents > 0
      ? Math.max(
          0,
          Math.min(
            requestedPlatformCents,
            Math.round((customEntryCents / requestedEntryCents) * requestedPlatformCents)
          )
        )
      : 0;
  const customTotalCents = customEntryCents + customPlatformCents;
  const approveTotalCents = useCustomAmount ? customTotalCents : requestedTotalCents;
  const customAmountInvalid =
    useCustomAmount &&
    (customEntryCents <= 0 || customEntryCents > requestedEntryCents);

  if (!request) {
    return null;
  }

  const handleApproveConfirm = async () => {
    if (customAmountInvalid) {
      toast.show({
        title: "Approval Failed",
        content: `Enter an amount greater than 0 and up to ${money.format(maxCustomAmount)}.`,
        duration: 6000,
        type: "fail",
      });
      return;
    }

    setProcessing(true);
    try {
      await apiService.approveRefundRequest(eventId, request._id, {
        remove_entry: removeEntry,
        ...(useCustomAmount
          ? {
              amount: customTotalCents / 100,
              entry_amount: customEntryCents / 100,
            }
          : {}),
      });
      toggle();
      toast.show({
        title: "Refund Approved",
        content: `Refund for ${request.dog_name} has been approved and processed.`,
        duration: 6000,
        type: "success",
      });
      onRefresh();
    } catch (e: any) {
      toast.show({
        title: "Approval Failed",
        content: e?.response?.data?.message ?? "Something went wrong. Please try again.",
        duration: 6000,
        type: "fail",
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleApproveClick = () => {
    setRemoveEntry(true);
    setUseCustomAmount(false);
    setCustomAmount("");
    toggle();
  };

  const handleDeny = async () => {
    setProcessing(true);
    try {
      await apiService.denyRefundRequest(eventId, request._id);
      toast.show({
        title: "Refund Denied",
        content: `Refund request for ${request.dog_name} has been denied.`,
        duration: 6000,
        type: "success",
      });
      onRefresh();
    } catch (e: any) {
      toast.show({
        title: "Denial Failed",
        content: e?.response?.data?.message ?? "Something went wrong. Please try again.",
        duration: 6000,
        type: "fail",
      });
    } finally {
      setProcessing(false);
    }
  };

  const menuOptions: IContextMenuOption[] = [];

  if (isRequested) {
    menuOptions.push(
      { label: "Approve Refund", onClick: handleApproveClick },
      { label: "Deny Refund", onClick: handleDeny },
    );
  }

  if (hasEntryForms && eventId && request.dog_id) {
    menuOptions.push({
      label: "Download Registration Form(s)",
      onClick: () => {
        download.pushDownload({
          type: "registration",
          registrantId: eventId,
          dogId: request.dog_id,
        });
      },
    });
  }

  if (menuOptions.length === 0) {
    return null;
  }

  return (
    <div>
      <ContextMenu options={processing ? [] : menuOptions} />
      <Modal
        isShown={isShown}
        hide={toggle}
        className="approveRefundModal"
        modalContent={
          <ApproveConfirmModal
            request={request}
            currency={currency}
            money={money}
            removeEntry={removeEntry}
            onToggleRemoveEntry={() => setRemoveEntry((prev) => !prev)}
            useCustomAmount={useCustomAmount}
            onToggleCustomAmount={() => setUseCustomAmount((prev) => !prev)}
            customAmount={customAmount}
            onCustomAmountChange={setCustomAmount}
            customEntryCents={customEntryCents}
            customPlatformCents={customPlatformCents}
            approveTotalCents={approveTotalCents}
            maxCustomAmount={maxCustomAmount}
            feesChargedToCustomer={feesChargedToCustomer}
            customAmountInvalid={customAmountInvalid}
            onConfirm={handleApproveConfirm}
            onCancel={toggle}
            processing={processing}
          />
        }
      />
    </div>
  );
};
